import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { onlineStatus, onlineColor, smartTime } from '../utils/api';
import { IcX, IcMsg } from './Icons';

/* ── tiny inline icons ────────────────────────────────────────────── */
const IcEye = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IcUser = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IcCalendar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

/* ── skeleton shimmer block ───────────────────────────────────────── */
const Shimmer = ({ w = '100%', h = 14, r = 8, mb = 0 }) => (
  <div style={{ width: w, height: h, borderRadius: r, marginBottom: mb, background: 'linear-gradient(90deg, var(--nat-s) 25%, var(--nat-d) 50%, var(--nat-s) 75%)', backgroundSize: '200% 100%', animation: 'shimmerSlide 1.4s linear infinite' }} />
);

/* ── decorative SVG arc for banner ───────────────────────────────── */
const BannerArc = ({ isTutor }) => (
  <svg style={{ position: 'absolute', bottom: 0, right: 0, opacity: 0.12, pointerEvents: 'none' }} width="160" height="96" viewBox="0 0 160 96" fill="none">
    <circle cx="140" cy="96" r="80" stroke="white" strokeWidth="28"/>
    <circle cx="140" cy="96" r="48" stroke="white" strokeWidth="16"/>
  </svg>
);

export default function MiniProfilePopup({ userId, onClose }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgBig, setImgBig] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setUser(null);
    api.get(`/users/${userId}`)
      .then(r => setUser(r.data.user || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  const name     = user ? (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—') : '—';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const isSelf   = currentUser?._id === userId;
  const status   = isSelf ? 'online' : onlineStatus(user?.lastSeen);
  const isTutor  = user?.role === 'tutor';

  const ringColor  = status === 'online' ? '#22c55e' : status === 'recent' ? '#f59e0b' : '#9ca3af';
  const statusText = status === 'online' ? 'Online now' : status === 'recent' ? 'Recently online' : user?.lastSeen ? `Last seen ${smartTime(user.lastSeen)}` : 'Offline';

  /* course pills */
  const courses    = Array.isArray(user?.courses) ? user.courses : [];
  const maxCourses = isTutor ? 5 : 4;
  const shownCourses = courses.slice(0, maxCourses);
  const extraCourses = courses.length > maxCourses ? courses.length - maxCourses : 0;

  /* navigation helpers */
  const goFull    = () => { onClose(); navigate(`/${isTutor ? 'tutor' : 'student'}/${userId}`); };
  const goMsg     = () => { onClose(); navigate(`/messages?user=${userId}`); };
  const goAccount = () => { onClose(); navigate('/account'); };
  const goTT      = () => { onClose(); navigate(`/tutor/${userId}?tab=timetable`); };

  /* banner colours */
  const bannerGrad = isTutor
    ? 'linear-gradient(145deg, #060e1f 0%, #0c1428 55%, #162244 100%)'
    : 'linear-gradient(145deg, #1a0800 0%, #8a3200 55%, #c24d00 100%)';

  /* role pill colours */
  const roleBg   = isTutor ? '#0d2240'  : '#c24d00';
  const roleText = isTutor ? '#7eb4f7'  : '#ffd4a8';

  /* solid cta colour */
  const ctaBg     = isTutor ? 'linear-gradient(135deg, #0c1428 0%, #1a2744 100%)' : 'var(--org)';
  const ctaShadow = isTutor ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(255,106,0,0.35)';

  return (
    <>
      <style>{`
        @keyframes miniPopIn {
          from { opacity: 0; transform: scale(0.88) translateY(20px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
        @keyframes livePulse {
          0%,100% { box-shadow: 0 0 0 0   ${ringColor}70; }
          50%     { box-shadow: 0 0 0 8px ${ringColor}00; }
        }
        @keyframes shimmerSlide {
          from { background-position: 200% 0; }
          to   { background-position: -200% 0; }
        }
        .mini-ghost-btn {
          flex: 1;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          background: transparent;
          border: 1.5px solid var(--nat-d);
          border-radius: 12px;
          padding: 10px 0;
          font-size: 13px; font-weight: 700;
          color: var(--txt);
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        .mini-ghost-btn:hover { background: var(--nat-s); border-color: var(--txt-l); }
        .mini-cta-btn {
          flex: 1;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          border: none;
          border-radius: 12px;
          padding: 10px 0;
          font-size: 13px; font-weight: 700;
          color: #fff;
          cursor: pointer;
          transition: opacity 0.15s, transform 0.1s;
        }
        .mini-cta-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .mini-cta-btn:active { transform: translateY(0); }
        .mini-pill {
          display: inline-flex; align-items: center;
          background: var(--nat-s);
          border: 1px solid var(--nat-d);
          border-radius: 100px;
          padding: 3px 10px;
          font-size: 10px; font-weight: 700;
          color: var(--txt-m);
          white-space: nowrap;
          max-width: 100px;
          overflow: hidden; text-overflow: ellipsis;
        }
      `}</style>

      {/* Full-size avatar lightbox */}
      {imgBig && user?.profilePicture && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setImgBig(false)}
        >
          <button
            onClick={e => { e.stopPropagation(); setImgBig(false); }}
            style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <IcX size={20} />
          </button>
          <img
            src={user.profilePicture} alt={name}
            style={{ maxWidth: '82vw', maxHeight: '82vh', borderRadius: 16, objectFit: 'contain', boxShadow: '0 24px 80px rgba(0,0,0,0.8)' }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.45)' }}
        onClick={onClose}
      >
        {/* Card */}
        <div
          style={{ background: 'var(--white)', borderRadius: 22, width: '100%', maxWidth: 360, overflow: 'hidden', boxShadow: '0 28px 70px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)', animation: 'miniPopIn 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Banner ── */}
          <div style={{ height: 96, background: bannerGrad, position: 'relative', overflow: 'hidden' }}>
            <BannerArc isTutor={isTutor} />
            {/* watermark initials */}
            <div style={{ position: 'absolute', right: -14, top: -22, fontSize: 100, fontFamily: 'var(--display)', fontWeight: 900, color: 'rgba(255,255,255,0.04)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>{initials}</div>
            {/* close */}
            <button
              onClick={onClose}
              style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.14)', border: 'none', borderRadius: '50%', width: 30, height: 30, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
            >
              <IcX size={13} />
            </button>
          </div>

          {/* ── Body ── */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: -48, paddingBottom: 20 }}>

            {/* Avatar */}
            <div style={{ position: 'relative', marginBottom: 14, zIndex: 1 }}>
              <div
                style={{
                  width: 96, height: 96, borderRadius: '50%', overflow: 'hidden',
                  background: isTutor ? '#1e2d3d' : '#c24d00',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--display)', fontSize: 30, fontWeight: 700, color: '#fff',
                  border: '4px solid var(--white)',
                  boxShadow: `0 0 0 3px ${ringColor}, 0 10px 28px rgba(0,0,0,0.22)`,
                  cursor: user?.profilePicture ? 'zoom-in' : 'default',
                  animation: status === 'online' ? 'livePulse 2.2s ease-in-out infinite' : 'none',
                  flexShrink: 0,
                }}
                onClick={() => user?.profilePicture && setImgBig(true)}
              >
                {loading
                  ? <div className="spinner" style={{ width: 24, height: 24, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                  : user?.profilePicture
                    ? <img src={user.profilePicture} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initials
                }
              </div>
            </div>

            {/* ── Skeleton loading ── */}
            {loading && (
              <div style={{ width: '100%', padding: '0 24px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <Shimmer w="55%" h={18} r={10} />
                <Shimmer w="38%" h={12} r={8} />
                <Shimmer w="70%" h={12} r={8} />
                <div style={{ height: 8 }} />
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <Shimmer w="33%" h={52} r={12} />
                  <Shimmer w="33%" h={52} r={12} />
                  <Shimmer w="33%" h={52} r={12} />
                </div>
                <div style={{ height: 4 }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Shimmer w={68} h={24} r={100} />
                  <Shimmer w={82} h={24} r={100} />
                  <Shimmer w={60} h={24} r={100} />
                </div>
                <div style={{ height: 8 }} />
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <Shimmer w="50%" h={40} r={12} />
                  <Shimmer w="50%" h={40} r={12} />
                </div>
              </div>
            )}

            {/* ── Error state ── */}
            {!loading && !user && (
              <div style={{ padding: '24px 0', fontSize: 13, color: 'var(--txt-l)', textAlign: 'center' }}>Could not load profile</div>
            )}

            {/* ── Loaded content ── */}
            {!loading && user && (
              <>
                {/* Name */}
                <div style={{ fontFamily: 'var(--display)', fontSize: 19, fontWeight: 800, color: 'var(--txt)', marginBottom: 4, textAlign: 'center', padding: '0 20px', lineHeight: 1.2 }}>{name}</div>

                {/* Tutorial ID (students) / Title (tutors) */}
                {isTutor && user.title && (
                  <div style={{ fontSize: 12, color: 'var(--txt-m)', fontWeight: 600, marginBottom: 8, fontStyle: 'italic' }}>{user.title}</div>
                )}
                {!isTutor && user.tutorialId && (
                  <div style={{ fontSize: 11, color: 'var(--txt-l)', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.5px', marginBottom: 8 }}>{user.tutorialId}</div>
                )}

                {/* Role pill + Faculty/Dept pill row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'center', padding: '0 16px' }}>
                  <span style={{ background: roleBg, color: roleText, fontSize: 9, fontWeight: 800, padding: '4px 10px', borderRadius: 100, letterSpacing: '0.6px' }}>{isTutor ? 'TUTOR' : 'STUDENT'}</span>
                  {user.faculty && <span style={{ background: 'var(--nat-s)', border: '1px solid var(--nat-d)', color: 'var(--txt-m)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 100 }}>{user.faculty}</span>}
                  {user.department && <span style={{ background: 'var(--nat-s)', border: '1px solid var(--nat-d)', color: 'var(--txt-m)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 100 }}>{user.department}</span>}
                </div>

                {/* Online status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, fontSize: 12, fontWeight: 600, color: ringColor }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: ringColor, border: '2px solid var(--white)', boxShadow: `0 0 0 1px ${ringColor}55`, flexShrink: 0 }} />
                  {statusText}
                </div>

                {/* ── Stats grid 3-up ── */}
                {(user.level || user.hall || user.semester || user.experience || (Array.isArray(user.courses) && user.courses.length)) && (
                  <div style={{ display: 'flex', width: '100%', borderTop: '1px solid var(--nat-d)', borderBottom: '1px solid var(--nat-d)', marginBottom: 16 }}>
                    {(() => {
                      const entries = isTutor
                        ? [
                            user.level                   && ['Level',    user.level],
                            courses.length               && ['Courses',  courses.length],
                            user.experience              && ['Exp.',     user.experience],
                          ]
                        : [
                            user.level                   && ['Level',    user.level],
                            user.semester                && ['Semester', user.semester],
                            user.hall                    && ['Hall',     user.hall],
                          ];
                      return entries.filter(Boolean).slice(0, 3).map(([lbl, val], i, arr) => (
                        <div key={lbl} style={{ flex: 1, textAlign: 'center', padding: '11px 6px', borderRight: i < arr.length - 1 ? '1px solid var(--nat-d)' : 'none' }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
                          <div style={{ fontSize: 9, color: 'var(--txt-xl)', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 2 }}>{lbl}</div>
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {/* ── Courses/Subjects pills ── */}
                {shownCourses.length > 0 && (
                  <div style={{ padding: '0 16px', marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: 'var(--txt-xl)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>{isTutor ? 'Subjects Taught' : 'Enrolled Courses'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {shownCourses.map((c, i) => (
                        <span key={i} className="mini-pill">
                          {typeof c === 'string' ? c : c.code || c.name || c.title || '—'}
                        </span>
                      ))}
                      {extraCourses > 0 && (
                        <span className="mini-pill" style={{ background: isTutor ? '#0d2240' : '#2d1500', color: isTutor ? '#7eb4f7' : '#ffb07a', border: 'none' }}>+{extraCourses} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* ── GPA badge (students) ── */}
                {!isTutor && user.cgpa != null && (
                  <div style={{ marginBottom: 14 }}>
                    <span style={{ background: 'linear-gradient(135deg, #0f5132 0%, #198754 100%)', color: '#d4edda', fontSize: 11, fontWeight: 800, padding: '5px 14px', borderRadius: 100, letterSpacing: '0.3px' }}>
                      GPA: {Number(user.cgpa).toFixed(2)}
                    </span>
                  </div>
                )}

                {/* ── Bio (tutors) ── */}
                {isTutor && user.bio && (
                  <div style={{ padding: '0 20px', marginBottom: 14, width: '100%', boxSizing: 'border-box' }}>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--txt-m)', fontStyle: 'italic', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      "{user.bio}"
                    </p>
                  </div>
                )}

                {/* ── Action buttons ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {isSelf ? (
                      <>
                        <button className="mini-ghost-btn" onClick={goAccount}>
                          <IcUser size={14} /> My Account
                        </button>
                        <button className="mini-cta-btn" style={{ background: ctaBg, boxShadow: ctaShadow }} onClick={goFull}>
                          <IcEye size={14} /> My Profile
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="mini-ghost-btn" onClick={goMsg}>
                          <IcMsg size={14} /> Message
                        </button>
                        <button className="mini-cta-btn" style={{ background: ctaBg, boxShadow: ctaShadow }} onClick={goFull}>
                          <IcEye size={14} /> View Profile
                        </button>
                      </>
                    )}
                  </div>

                  {/* Timetable button for tutors */}
                  {isTutor && (
                    <button className="mini-ghost-btn" style={{ flex: 'unset', width: '100%' }} onClick={goTT}>
                      <IcCalendar size={14} /> View Timetable
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
